import { api } from '@goaltree/backend/convex/_generated/api'
import { useConvex } from 'convex/react'
import { useCallback, useEffect, useRef } from 'react'

import { getUsageLimit, handleUsageLimit } from '@/lib/usage-limit'
import type { OfflineOp, TaskStatus } from '@/store/offline.store'
import { isTempId, useOfflineStore } from '@/store/offline.store'

import { useOnlineStatus } from './use-online-status'

// ── Status folding ────────────────────────────────────────────────────────────
// Reduce the queue to a final status/completion per task id, so a list can be
// projected in one pass regardless of how many ops touch the same task.

type StatusResult = { status?: TaskStatus; completed?: boolean }

function foldStatusByTask(queue: OfflineOp[]): Map<string, StatusResult> {
  const byTask = new Map<string, StatusResult>()
  for (const op of queue) {
    if (op.kind === 'complete') {
      byTask.set(op.args.taskId, { status: 'done', completed: true })
    } else if (op.kind === 'uncomplete') {
      byTask.set(op.args.taskId, { status: 'backlog', completed: false })
    } else if (op.kind === 'setTaskStatus') {
      byTask.set(op.args.taskId, {
        status: op.args.status,
        completed: op.args.status === 'done',
      })
    }
  }
  return byTask
}

// ── Projections ─────────────────────────────────────────────────────────────
// Apply the pending queue on top of a (live-or-cached) read so the UI reflects
// offline actions even after a reload, before they've been replayed to Convex.

type TodayLike = { taskId: string; status: string; completedAt: number | null }

export function projectTodayTasks<T extends TodayLike>(tasks: T[], queue: OfflineOp[]): T[] {
  if (!queue.length) return tasks
  // Tasks skipped offline drop off Today (until an unskip re-adds them).
  const skipped = new Set<string>()
  for (const op of queue) {
    if (op.kind === 'skipToday') skipped.add(op.args.taskId)
    else if (op.kind === 'unskipToday') skipped.delete(op.args.taskId)
  }
  const base = skipped.size ? tasks.filter((t) => !skipped.has(t.taskId)) : tasks
  const byTask = foldStatusByTask(queue)
  if (!byTask.size) return base
  return base.map((t) => {
    const r = byTask.get(t.taskId)
    if (!r) return t
    return {
      ...t,
      ...(r.status ? { status: r.status } : {}),
      completedAt: r.completed
        ? (t.completedAt ?? Date.now())
        : r.completed === false
          ? null
          : t.completedAt,
    }
  })
}

type TreeTaskLike = { _id: string; status?: string; completedAt: number | null }
type GoalTreeLike = {
  milestones: { targets: { tasks: TreeTaskLike[] }[] }[]
}

export function projectGoalTree<T extends GoalTreeLike>(tree: T, queue: OfflineOp[]): T {
  if (!queue.length) return tree
  const byTask = foldStatusByTask(queue)
  if (!byTask.size) return tree
  const apply = (task: TreeTaskLike): TreeTaskLike => {
    const r = byTask.get(task._id)
    if (!r) return task
    return {
      ...task,
      ...(r.status ? { status: r.status } : {}),
      completedAt: r.completed
        ? (task.completedAt ?? Date.now())
        : r.completed === false
          ? null
          : task.completedAt,
    }
  }
  return {
    ...tree,
    milestones: tree.milestones.map((ms) => ({
      ...ms,
      targets: ms.targets.map((tg) => ({ ...tg, tasks: tg.tasks.map(apply) })),
    })),
  }
}

type InboxLike = {
  _id: string
  status?: string
  completedAt: number | null
  isHabit?: boolean
  pinnedAt?: number
  effort?: 'low' | 'medium' | 'high'
}

export function projectInboxTasks<T extends InboxLike>(
  tasks: T[],
  parentId: string,
  queue: OfflineOp[],
  makeSynthetic: (op: Extract<OfflineOp, { kind: 'createTask' }>) => T,
): T[] {
  if (!queue.length) return tasks
  const byTask = foldStatusByTask(queue)
  // Last-write-wins habit flag per task.
  const habitByTask = new Map<string, boolean>()
  for (const op of queue) {
    if (op.kind === 'setIsHabit') habitByTask.set(op.args.taskId, op.args.isHabit)
  }
  // Last-write-wins effort per task.
  const effortByTask = new Map<
    string,
    { effort: 'low' | 'medium' | 'high'; superUrgent: boolean }
  >()
  for (const op of queue) {
    if (op.kind === 'setEffort')
      effortByTask.set(op.args.taskId, {
        effort: op.args.effort,
        superUrgent: !!op.args.superUrgent,
      })
  }
  // Pin toggles fold to a parity per task (odd = flipped from the base state).
  const pinToggle = new Map<string, { count: number; at: number }>()
  for (const op of queue) {
    if (op.kind === 'togglePin') {
      const e = pinToggle.get(op.args.taskId)
      pinToggle.set(op.args.taskId, { count: (e?.count ?? 0) + 1, at: op.createdAt })
    }
  }
  const creates = queue.filter(
    (o): o is Extract<OfflineOp, { kind: 'createTask' }> =>
      o.kind === 'createTask' && o.args.parentId === parentId,
  )
  const combined = creates.length ? [...creates.map(makeSynthetic), ...tasks] : tasks
  if (!byTask.size && !habitByTask.size && !pinToggle.size && !effortByTask.size) return combined
  return combined.map((t) => {
    const r = byTask.get(t._id)
    const habit = habitByTask.get(t._id)
    const effort = effortByTask.get(t._id)
    const pin = pinToggle.get(t._id)
    const flipped = pin && pin.count % 2 === 1
    if (!r && habit === undefined && effort === undefined && !flipped) return t
    return {
      ...t,
      ...(habit !== undefined ? { isHabit: habit } : {}),
      ...(effort !== undefined
        ? { effort: effort.effort, superUrgent: effort.superUrgent || undefined }
        : {}),
      ...(flipped ? { pinnedAt: t.pinnedAt == null ? pin!.at : undefined } : {}),
      ...(r?.status ? { status: r.status } : {}),
      completedAt: r?.completed
        ? (t.completedAt ?? Date.now())
        : r?.completed === false
          ? null
          : t.completedAt,
    }
  })
}

// ── Replay ──────────────────────────────────────────────────────────────────

const MAX_TRIES = 5

/**
 * Drains the offline queue against Convex whenever connectivity returns and the
 * session is authenticated. Mount once, high in the authenticated tree.
 *
 * Ops are retried across passes rather than dropped on the first failure (a
 * failure is usually a still-settling reconnect / transient auth). An op is
 * only discarded after MAX_TRIES so a genuinely-bad op can't wedge the queue.
 */
export function useOfflineReplay(enabled: boolean) {
  const convex = useConvex()
  const online = useOnlineStatus()
  const draining = useRef(false)
  const triesById = useRef<Map<string, number>>(new Map())
  // Maps a temp task id (from an offline create) to the real id once replayed,
  // so a queued status change on a just-created task targets the right row.
  const tempToReal = useRef<Map<string, string>>(new Map())

  const drain = useCallback(async () => {
    if (draining.current) return
    draining.current = true
    try {
      // Process oldest-first, re-reading after each op so enqueues during a
      // drain aren't lost.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { queue, remove } = useOfflineStore.getState()
        const op = queue[0]
        if (!op) break
        try {
          await replayOp(convex, op, tempToReal.current)
          triesById.current.delete(op.id)
          remove(op.id)
        } catch (err) {
          // A plan cap won't lift on retry; drop the op and show the upgrade prompt.
          if (getUsageLimit(err)) {
            handleUsageLimit(err)
            triesById.current.delete(op.id)
            remove(op.id)
            continue
          }
          const tries = (triesById.current.get(op.id) ?? 0) + 1
          triesById.current.set(op.id, tries)
          if (tries >= MAX_TRIES) {
            triesById.current.delete(op.id)
            remove(op.id)
          }
          // Stop this pass either way — remaining ops retry on the next tick.
          break
        }
      }
    } finally {
      draining.current = false
    }
  }, [convex])

  const pending = useOfflineStore((s) => s.queue.length)
  useEffect(() => {
    if (!enabled || !online || pending === 0) return
    void drain()
    // Retry loop for ops that failed a pass (still-settling reconnect / auth).
    const timer = setInterval(() => void drain(), 8000)
    return () => clearInterval(timer)
  }, [enabled, online, pending, drain])
}

function resolveId(taskId: string, tempToReal: Map<string, string>): string {
  if (isTempId(taskId)) {
    const real = tempToReal.get(taskId)
    if (!real) throw new Error('temp task not yet created')
    return real
  }
  return taskId
}

async function replayOp(
  convex: ReturnType<typeof useConvex>,
  op: OfflineOp,
  tempToReal: Map<string, string>,
) {
  if (op.kind === 'complete') {
    await convex.mutation(api.nodes.complete, {
      taskId: resolveId(op.args.taskId, tempToReal) as never,
    })
  } else if (op.kind === 'uncomplete') {
    await convex.mutation(api.nodes.uncomplete, {
      taskId: resolveId(op.args.taskId, tempToReal) as never,
    })
  } else if (op.kind === 'setTaskStatus') {
    await convex.mutation(api.nodes.setTaskStatus, {
      taskId: resolveId(op.args.taskId, tempToReal) as never,
      status: op.args.status,
    })
  } else if (op.kind === 'setIsHabit') {
    await convex.mutation(api.nodes.setIsHabit, {
      taskId: resolveId(op.args.taskId, tempToReal) as never,
      isHabit: op.args.isHabit,
    })
  } else if (op.kind === 'setEffort') {
    await convex.mutation(api.nodes.updateTaskEffort, {
      taskId: resolveId(op.args.taskId, tempToReal) as never,
      effort: op.args.effort,
      ...(op.args.superUrgent ? { superUrgent: true } : {}),
    })
  } else if (op.kind === 'skipToday') {
    await convex.mutation(api.dailySurface.skipToday, {
      taskId: resolveId(op.args.taskId, tempToReal) as never,
      ...(op.args.localDate ? { localDate: op.args.localDate } : {}),
      ...(op.args.reason ? { reason: op.args.reason } : {}),
    })
  } else if (op.kind === 'unskipToday') {
    await convex.mutation(api.dailySurface.unskipToday, {
      taskId: resolveId(op.args.taskId, tempToReal) as never,
      ...(op.args.localDate ? { localDate: op.args.localDate } : {}),
    })
  } else if (op.kind === 'skipHabitToday') {
    await convex.mutation(api.dailySurface.skipHabitToday, {
      habitId: resolveId(op.args.habitId, tempToReal) as never,
      ...(op.args.localDate ? { localDate: op.args.localDate } : {}),
      ...(op.args.reason ? { reason: op.args.reason } : {}),
    })
  } else if (op.kind === 'unskipHabitToday') {
    await convex.mutation(api.dailySurface.unskipHabitToday, {
      habitId: resolveId(op.args.habitId, tempToReal) as never,
      ...(op.args.localDate ? { localDate: op.args.localDate } : {}),
    })
  } else if (op.kind === 'togglePin') {
    await convex.mutation(api.nodes.togglePin, {
      taskId: resolveId(op.args.taskId, tempToReal) as never,
    })
  } else if (op.kind === 'createTask') {
    const a = op.args
    const realId = await convex.mutation(api.nodes.createQuickTask, {
      title: a.title,
      ...(a.description ? { description: a.description } : {}),
      ...(a.tags?.length ? { tags: a.tags } : {}),
      ...(a.effort ? { effort: a.effort } : {}),
      ...(a.recursPeriod ? { recursPeriod: a.recursPeriod as never } : {}),
      ...(a.recursDays?.length ? { recursDays: a.recursDays } : {}),
      ...(a.recurrenceTime ? { recurrenceTime: a.recurrenceTime } : {}),
      ...(a.recurrenceTzOffset !== undefined ? { recurrenceTzOffset: a.recurrenceTzOffset } : {}),
      ...(a.recurrenceDay !== undefined ? { recurrenceDay: a.recurrenceDay } : {}),
      ...(a.durationMinutes !== undefined ? { durationMinutes: a.durationMinutes } : {}),
      ...(a.dueDate !== undefined ? { dueDate: a.dueDate } : {}),
      ...(a.isHabit ? { isHabit: a.isHabit } : {}),
      ...(a.category ? { category: a.category } : {}),
      ...(a.measure ? { measure: a.measure } : {}),
      ...(a.icon ? { icon: a.icon } : {}),
    })
    tempToReal.set(a.tempId, realId as unknown as string)
  }
}
